import { useEffect, useState } from 'react';

/** Reactively track a CSS media query. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Breakpoint from the design contract: mobile is < 768px, PLUS any short
 * viewport under tablet width.
 *
 * The second clause is the landscape-phone fix (owner 2026-07-27): rotating an
 * iPhone 14 gives 844×390, so on width alone the phone fell OUT of the mobile
 * tree and got the desktop Header + Footer + three-pane layout crammed into a
 * 390px-tall viewport — "header and footer take up majority of screen ...
 * content can't actually be viewed". 560px clears every phone in landscape
 * (Pro Max is 430 tall) and no tablet (iPad mini landscape is 744); the 1023px
 * width bound keeps a short desktop WINDOW on the desktop layout.
 *
 * MUST stay in lockstep with the CSS. Every mobile block in the stylesheets is
 * written as the same pair, and the tablet block at sizepaper.css carries the
 * matching `(min-height: 561px)` so the two can never half-apply.
 */
export const MOBILE_QUERY = '(max-width: 767px), (max-height: 560px) and (max-width: 1023px)';

export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}
